import React, { useState, useEffect } from 'react';
import { MdClose } from 'react-icons/md';
import { FiBell, FiMail, FiSmartphone, FiUsers, FiUpload, FiSearch } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { useStateContext } from '../contexts/ContextProvider';
import { toast } from 'react-toastify';

const ONE_MB = 1024 * 1024;
// The same limit as POST /schools/:schoolId/send-custom-email.
const MAX_ATTACHMENT_BYTES = 2 * ONE_MB;

const AnnouncementModal = ({ closeModal, editingAnnouncement, notificationType = 'app', onSuccess }) => {
  const { school } = useStateContext();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [audience, setAudience] = useState({
    parents: false,
    teachers: false,
    all: false
  });
  
  // Email specific fields
  const [recipients, setRecipients] = useState([]);
  const [attachments, setAttachments] = useState([]);
  
  // SMS specific fields
  const [recipientGroup, setRecipientGroup] = useState('all_parents');
  const [customRecipients, setCustomRecipients] = useState('');
  const [chargeFrom, setChargeFrom] = useState('card');
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  // Parents data for recipients
  const [parents, setParents] = useState([]);
  const [filteredParents, setFilteredParents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [parentsLoading, setParentsLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState('all');
  const selectedClass = selectedClassId === 'all' ? null : classes.find((cls) => cls._id === selectedClassId);

  // Fetch parents data when component mounts
  useEffect(() => {
    if ((notificationType === 'email' || notificationType === 'sms') && school?._id) {
      fetchParents(selectedClassId);
    }
  }, [notificationType, school?._id, selectedClassId]);

  useEffect(() => {
    if (school?.code || school?._id) {
      fetchClasses();
    }
  }, [school?.code, school?._id]);

  // Filter parents based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredParents(parents);
    } else {
      const filtered = parents.filter(parent => 
        parent.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.mobile_number?.includes(searchTerm)
      );
      setFilteredParents(filtered);
    }
  }, [searchTerm, parents]);

  const fetchParents = async (classId = 'all') => {
    if (!school?._id) return;
    try {
      setParentsLoading(true);
      const params = new URLSearchParams({ limit: '1000' });
      if (classId && classId !== 'all') {
        params.append('class_id', classId);
      }
      const endpoint = `/schools/${school._id}/parents?${params.toString()}`;
      const { data } = await userRequest.get(endpoint);
      const parentsData = Array.isArray(data?.parents) ? data.parents : Array.isArray(data) ? data : [];
      setParents(parentsData);
      setFilteredParents(parentsData);
      setSelectedRecipients(prev => prev.filter(id => parentsData.some(parent => parent._id === id)));
    } catch (error) {
      console.error('Error fetching parents:', error);
      toast.error('Failed to fetch parents data');
      setParents([]);
      setFilteredParents([]);
      setSelectedRecipients([]);
    } finally {
      setParentsLoading(false);
    }
  };

  const fetchClasses = async () => {
    if (!school?.code && !school?._id) return;
    try {
      setClassesLoading(true);
      let endpoint = '';
      if (school?.code) {
        endpoint = `/classes?code=${school.code}`;
      } else {
        endpoint = `/classes/school/${school._id}`;
      }
      const { data } = await userRequest.get(endpoint);
      const classesData = Array.isArray(data?.classes) ? data.classes : Array.isArray(data) ? data : [];
      setClasses(classesData);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Failed to fetch classes data');
      setClasses([]);
    } finally {
      setClassesLoading(false);
    }
  };

  useEffect(() => {
    if (editingAnnouncement) {
      if (notificationType === 'app') {
        setTitle(editingAnnouncement.title);
        setContent(editingAnnouncement.content);
        const newAudience = {
          parents: false,
          teachers: false,
          all: false
        };
        editingAnnouncement.audience.forEach(aud => {
          newAudience[aud] = true;
        });
        setAudience(newAudience);
      } else if (notificationType === 'email') {
        setSubject(editingAnnouncement.metadata?.subject || '');
        setMessage(editingAnnouncement.message);
        setRecipients(editingAnnouncement.metadata?.recipients || []);
        setSelectedRecipients(editingAnnouncement.metadata?.recipients || []);
      } else if (notificationType === 'sms') {
        setMessage(editingAnnouncement.message);
        setRecipientGroup(editingAnnouncement.recipient_group || 'all_parents');
        setCustomRecipients(editingAnnouncement.metadata?.recipients?.join(', ') || '');
        setChargeFrom(editingAnnouncement.charge_from || 'wallet');
        setSelectedRecipients(editingAnnouncement.metadata?.recipients || []);
      }
    }
  }, [editingAnnouncement, notificationType]);

  const handleAudienceChange = (group) => {
    setAudience(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const tooLarge = files.filter(file => file.size > MAX_ATTACHMENT_BYTES);
    setError(
      tooLarge
        .map(file => `${file.name} is ${(file.size / ONE_MB).toFixed(1)} MB. Each attachment can be up to 2 MB.`)
        .join(' ')
    );
    setAttachments(prev => [...prev, ...files.filter(file => file.size <= MAX_ATTACHMENT_BYTES)]);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleRecipientSelection = (parentId, isSelected) => {
    if (isSelected) {
      setSelectedRecipients(prev => [...prev, parentId]);
    } else {
      setSelectedRecipients(prev => prev.filter(id => id !== parentId));
    }
  };

  const handleSelectAllRecipients = () => {
    if (selectedRecipients.length === filteredParents.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(filteredParents.map(parent => parent._id));
    }
  };

  const handleClassFilterChange = (event) => {
    const newClassId = event.target.value;
    setSelectedClassId(newClassId);
    setSearchTerm('');
    // selections will be reconciled when new parents load, but clear immediately for UX
    setSelectedRecipients([]);
  };

  const getSelectedRecipientsData = () => {
    return parents.filter(parent => selectedRecipients.includes(parent._id));
  };

  const getRecipientOptions = () => {
    return [
      { value: 'all_parents', label: 'All Parents' },
      { value: 'all_teachers', label: 'All Teachers' },
      { value: 'custom', label: 'Custom Recipients' }
    ];
  };

  useEffect(() => {
    if (notificationType === 'sms' && recipientGroup !== 'custom') {
      setSelectedClassId('all');
      setSearchTerm('');
      setSelectedRecipients([]);
    }
  }, [recipientGroup, notificationType]);

  const handleChargeFromChange = (value) => {
    setChargeFrom(value);
    if (value === 'card' && !school?.subscription) {
      setShowAddCardModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (notificationType === 'app') {
        // App notification validation
        if (!title.trim() || !content.trim()) {
          setError('Please fill in all required fields');
          setLoading(false);
          return;
        }

        const selectedAudience = Object.entries(audience)
          .filter(([_, isSelected]) => isSelected)
          .map(([group]) => group);

        if (selectedAudience.length === 0) {
          setError('Please select at least one audience');
          setLoading(false);
          return;
        }

        if (editingAnnouncement) {
          await userRequest.put(`/announcement/${school._id}/${editingAnnouncement._id}`, {
            title,
            content,
            is_active: editingAnnouncement.is_active,
            audience: selectedAudience
          });
          toast.success('App notification updated successfully');
        } else {
          await userRequest.post(`/announcement/${school._id}`, {
            title,
            content,
            is_active: true,
            audience: selectedAudience
          });
          toast.success('App notification sent successfully');
        }
      } else if (notificationType === 'email') {
        // Email validation
        if (!subject.trim() || !message.trim() || selectedRecipients.length === 0) {
          setError('Please fill in all required fields and select at least one recipient');
          setLoading(false);
          return;
        }

        const selectedParentsData = getSelectedRecipientsData();
        const recipientEmails = selectedParentsData.map(parent => parent.email).filter(email => email);

        if (recipientEmails.length === 0) {
          setError('Selected recipients do not have valid email addresses');
          setLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append('subject', subject);
        formData.append('message', message);
        formData.append('recipients', JSON.stringify(recipientEmails));
        
        if (attachments.length > 0) {
          attachments.forEach(file => {
            formData.append('attachments', file);
          });
        }

        await userRequest.post(`/schools/${school._id}/send-custom-email`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success('Email sent successfully');
      } else if (notificationType === 'sms') {
        // SMS validation
        if (!message.trim()) {
          setError('Please enter a message');
          setLoading(false);
          return;
        }

        if (chargeFrom === 'card' && !school?.subscription) {
          setLoading(false);
          setShowAddCardModal(true);
          toast.info('Please add your payment card in Settings to continue');
          return;
        }

        let finalRecipients = [];
        let smsData = {};

        if (recipientGroup === 'custom') {
          if (selectedRecipients.length === 0) {
            setError('Please select at least one recipient');
            setLoading(false);
            return;
          }
          const selectedParentsData = getSelectedRecipientsData();
          finalRecipients = selectedParentsData.map(parent => parent._id).filter(Boolean);

          if (finalRecipients.length === 0) {
            setError('Selected recipients are invalid');
            setLoading(false);
            return;
          }

          // For custom recipients, only send recipients field (IDs)
          smsData = {
            message: message,
            recipients: finalRecipients,
            charge_from: chargeFrom,
            callback_url: `${window.location.origin}/announcements?tab=sms&callback=success`
          };
        } else {
          // For predefined groups, only send recipient_group field
          smsData = {
            message: message,
            recipient_group: recipientGroup,
            charge_from: chargeFrom,
            callback_url: `${window.location.origin}/announcements?tab=sms&callback=success`
          };
        }

        await userRequest.post(`/schools/${school._id}/send-bulk-sms`, smsData);
        toast.success('SMS sent successfully');
      }

      setSuccess(true);
      
      setTimeout(() => {
        setSuccess(false);
        // Reset form based on notification type
        if (notificationType === 'app') {
          setTitle('');
          setContent('');
          setAudience({ parents: false, teachers: false, all: false });
        } else if (notificationType === 'email') {
          setSubject('');
          setMessage('');
          setSelectedRecipients([]);
          setAttachments([]);
        } else if (notificationType === 'sms') {
          setMessage('');
          setRecipientGroup('all_parents');
          setCustomRecipients('');
          setChargeFrom('wallet');
          setSelectedRecipients([]);
        }
        
        if (onSuccess) {
          onSuccess();
        } else {
          closeModal();
        }
      }, 2000);
    } catch (error) {
      // The API answers errors as a bare string, and requestMethods.js has already shown it as a toast.
      const data = error.response?.data;
      setError((typeof data === 'string' ? data : data?.message) || `Failed to process ${notificationType} notification`);
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    if (editingAnnouncement) {
      return `Edit ${notificationType === 'app' ? 'App Notification' : notificationType === 'email' ? 'Email' : 'SMS'}`;
    }
    return `Send ${notificationType === 'app' ? 'App Notification' : notificationType === 'email' ? 'Email' : 'SMS'}`;
  };

  const getSubmitButtonText = () => {
    if (loading) return 'Processing...';
    if (editingAnnouncement) {
      return `Update ${notificationType === 'app' ? 'App Notification' : notificationType === 'email' ? 'Email' : 'SMS'}`;
    }
    return `Send ${notificationType === 'app' ? 'App Notification' : notificationType === 'email' ? 'Email' : 'SMS'}`;
  };

  const renderAppNotificationForm = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
          placeholder="Enter announcement title"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Content
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent h-32"
          placeholder="Enter announcement content"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Audience
        </label>
        <div className="space-y-2">
          {Object.entries(audience).map(([group, isSelected]) => (
            <label key={group} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleAudienceChange(group)}
                className="rounded border-gray-300 text-[#3F3DA5] focus:ring-[#3F3DA5]"
              />
              <span className="text-sm text-gray-700 capitalize">{group}</span>
            </label>
          ))}
        </div>
      </div>
    </>
  );

  const renderEmailForm = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Subject
        </label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
          placeholder="Enter email subject"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent h-32"
          placeholder="Enter email message"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Recipients ({selectedRecipients.length} selected)
        </label>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-600">Filter by Class:</span>
            <select
              value={selectedClassId}
              onChange={handleClassFilterChange}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
            >
              <option value="all">All Classes</option>
              {classes.map((cls) => (
                <option key={cls._id} value={cls._id}>
                  {cls.class_name}
                </option>
              ))}
            </select>
            {classesLoading && (
              <span className="inline-flex items-center text-xs text-gray-500">
                <span className="w-3 h-3 mr-2 border-2 border-gray-300 border-t-[#3F3DA5] rounded-full animate-spin"></span>
                Loading classes...
              </span>
            )}
          </div>
          {selectedClass && (
            <span className="text-xs text-gray-500">
              Showing parents in {selectedClass.class_name}
            </span>
          )}
        </div>
        
        {/* Search and Select All */}
        <div className="mb-3 space-y-2">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
              placeholder="Search parents by name, email, or phone..."
            />
          </div>
          
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleSelectAllRecipients}
              className="text-sm text-[#3F3DA5] hover:text-[#2E2D7A] font-medium"
            >
              {selectedRecipients.length === filteredParents.length ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-sm text-gray-500">
              {filteredParents.length} parents found
            </span>
          </div>
        </div>

        {/* Recipients List */}
        {parentsLoading ? (
          <div className="border border-gray-300 rounded-lg max-h-48 flex flex-col items-center justify-center py-6 text-gray-500">
            <div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-[#3F3DA5] rounded-full animate-spin mb-2"></div>
            <span className="text-sm">
              Loading parents{selectedClass ? ` in ${selectedClass.class_name}` : ''}...
            </span>
          </div>
        ) : (
          <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
            {filteredParents.length > 0 ? (
              filteredParents.map((parent) => (
                <label key={parent._id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                  <input
                    type="checkbox"
                    checked={selectedRecipients.includes(parent._id)}
                    onChange={(e) => handleRecipientSelection(parent._id, e.target.checked)}
                    className="rounded border-gray-300 text-[#3F3DA5] focus:ring-[#3F3DA5]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {parent.firstname} {parent.lastname}
                      </span>
                      {!parent.email && (
                        <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded-full">
                          No Email
                        </span>
                      )}
                    </div>
                    {parent.email && (
                      <p className="text-xs text-gray-500 truncate">{parent.email}</p>
                    )}
                    {parent.mobile_number && (
                      <p className="text-xs text-gray-500 truncate">{parent.mobile_number}</p>
                    )}
                  </div>
                </label>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500">
                {searchTerm
                  ? 'No parents found matching your search'
                  : selectedClass
                    ? `No parents found for ${selectedClass.class_name}`
                    : 'No parents available'}
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Attachments
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            className="w-full"
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
          />
          {attachments.length > 0 && (
            <div className="mt-3 space-y-2">
              {attachments.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <span className="text-sm text-gray-600">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <MdClose />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderSMSForm = () => (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent h-32"
          placeholder="Enter SMS message"
        />
        {(() => {
          const specialCharsRegex = /[;^{}\\\[~\]|€'”`]/g;
          const containsSpecialChars = specialCharsRegex.test(message);
          const singleLimit = containsSpecialChars ? 70 : 160;
          const concatLimit = containsSpecialChars ? 67 : 153;
          const usesConcatenation = message.length > singleLimit;
          const perSegment = usesConcatenation ? concatLimit : singleLimit;
          const segments = message.length === 0 ? 0 : Math.ceil(message.length / perSegment);
          return (
            <div className="mt-2 flex items-start justify-between">
              <div className={`text-xs ${containsSpecialChars ? 'text-amber-700' : 'text-gray-500'}`}>
                {containsSpecialChars ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                    <span className="font-medium">Note on special characters:</span> 1 page = 70 characters. Your message content is {segments || 1} page{(segments || 1) > 1 ? 's' : ''} due to special characters.
                    <div className="mt-1">
                      Special characters include: <span className="font-mono">; ^ { } \\ [ ~ ] | € ' ” `</span>
                    </div>
                  </div>
                ) : (
                  <span>1 page = 160 characters</span>
                )}
              </div>
              <div className="text-xs text-gray-600 ml-4 whitespace-nowrap">
                {message.length} chars • {segments || 1} page{(segments || 1) > 1 ? 's' : ''} • {perSegment} chars/page
              </div>
            </div>
          );
        })()}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Recipient Group
        </label>
        <select
          value={recipientGroup}
          onChange={(e) => setRecipientGroup(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
        >
          {getRecipientOptions().map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {recipientGroup === 'custom' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Recipients ({selectedRecipients.length} selected)
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Filter by Class:</span>
              <select
                value={selectedClassId}
                onChange={handleClassFilterChange}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
              >
                <option value="all">All Classes</option>
                {classes.map((cls) => (
                  <option key={cls._id} value={cls._id}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
              {classesLoading && (
                <span className="inline-flex items-center text-xs text-gray-500">
                  <span className="w-3 h-3 mr-2 border-2 border-gray-300 border-t-[#3F3DA5] rounded-full animate-spin"></span>
                  Loading classes...
                </span>
              )}
            </div>
            {selectedClass && (
              <span className="text-xs text-gray-500">
                Showing parents in {selectedClass.class_name}
              </span>
            )}
          </div>
          
          {/* Search and Select All */}
          <div className="mb-3 space-y-2">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
                placeholder="Search parents by name, email, or phone..."
              />
            </div>
            
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={handleSelectAllRecipients}
                className="text-sm text-[#3F3DA5] hover:text-[#2E2D7A] font-medium"
              >
                {selectedRecipients.length === filteredParents.length ? 'Deselect All' : 'Select All'}
              </button>
              <span className="text-sm text-gray-500">
                {filteredParents.length} parents found
              </span>
            </div>
          </div>

          {/* Recipients List */}
          {parentsLoading ? (
            <div className="border border-gray-300 rounded-lg max-h-48 flex flex-col items-center justify-center py-6 text-gray-500">
              <div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-[#3F3DA5] rounded-full animate-spin mb-2"></div>
              <span className="text-sm">
                Loading parents{selectedClass ? ` in ${selectedClass.class_name}` : ''}...
              </span>
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
              {filteredParents.length > 0 ? (
                filteredParents.map((parent) => (
                  <label key={parent._id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.includes(parent._id)}
                      onChange={(e) => handleRecipientSelection(parent._id, e.target.checked)}
                      className="rounded border-gray-300 text-[#3F3DA5] focus:ring-[#3F3DA5]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {parent.firstname} {parent.lastname}
                        </span>
                        {!parent.mobile_number && (
                          <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded-full">
                            No Phone
                          </span>
                        )}
                      </div>
                      {parent.email && (
                        <p className="text-xs text-gray-500 truncate">{parent.email}</p>
                      )}
                      {parent.mobile_number && (
                        <p className="text-xs text-gray-500 truncate">{parent.mobile_number}</p>
                      )}
                    </div>
                  </label>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm
                    ? 'No parents found matching your search'
                    : selectedClass
                      ? `No parents found for ${selectedClass.class_name}`
                      : 'No parents available'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Charge From
        </label>
        <select
          value={chargeFrom}
          onChange={(e) => handleChargeFromChange(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F3DA5] focus:border-transparent"
        >
          {/*<option value="wallet">School Wallet</option>*/}
          <option value="card">Payment Card</option>
        </select>
      </div>
    </>
  );

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            {notificationType === 'app' && <FiBell className="text-2xl text-[#3F3DA5]" />}
            {notificationType === 'email' && <FiMail className="text-2xl text-[#3F3DA5]" />}
            {notificationType === 'sms' && <FiSmartphone className="text-2xl text-[#3F3DA5]" />}
            <h2 className="text-xl font-semibold text-gray-800">
              {getModalTitle()}
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {notificationType === 'app' && renderAppNotificationForm()}
          {notificationType === 'email' && renderEmailForm()}
          {notificationType === 'sms' && renderSMSForm()}

          {error && (
            <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">{error}</div>
          )}

          {success && (
            <div className="text-green-500 text-sm bg-green-50 p-3 rounded-lg">
              {notificationType === 'app' 
                ? (editingAnnouncement ? 'App notification updated successfully!' : 'App notification sent successfully!')
                : notificationType === 'email'
                ? 'Email sent successfully!'
                : 'SMS sent successfully!'
              }
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-[#3F3DA5] rounded-full hover:bg-[#2E2D7A] disabled:opacity-50"
            >
              {getSubmitButtonText()}
            </button>
          </div>
        </form>
      </div>
    </div>
    {showAddCardModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Add Payment Card</h3>
            <button onClick={() => setShowAddCardModal(false)} className="text-gray-500 hover:text-gray-700">
              <MdClose className="text-2xl" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            To send SMS and charge from a card, please add a payment card to your school account in Settings.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowAddCardModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200"
            >
              Close
            </button>
            <a
              href="/settings"
              className="px-4 py-2 text-sm font-medium text-white bg-[#3F3DA5] rounded-full hover:bg-[#2E2D7A]"
            >
              Go to Settings
            </a>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default AnnouncementModal; 